import { useEffect, useState } from 'react';
import { Alert, App, Button, Form, Input, Tabs } from 'antd';
import { api } from '../../api';
import { ErrorNotice, FormSkeleton, PageHeader, Panel } from '../../components';
import { timeAgo, useLoad } from '../../lib';
import { BackToSettings } from './BackToSettings';

const previewUrl = process.env.REACT_APP_KIVONEX_WEBSITE || (process.env.NODE_ENV === 'development' ? 'http://localhost:5174/' : 'https://kivonex.in/');

/** Settings › Legal pages: privacy, terms, refund and delivery wording stored in the database. */
export function LegalPagesSettings() {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [slug, setSlug] = useState('privacy');
  const [saving, setSaving] = useState(false);
  const { data, error, loading, reload } = useLoad(() => api('/v1/admin/legal'));
  const pages = data?.pages ?? [];
  const current = pages.find((page) => page.slug === slug) ?? pages[0];

  useEffect(() => {
    if (current) form.setFieldsValue({ title: current.title, body: current.body });
  }, [current, form]);

  async function save(values) {
    if (!current) return;
    setSaving(true);
    try {
      await api(`/v1/admin/legal/${current.slug}`, { method: 'PUT', body: JSON.stringify(values) });
      message.success(`${values.title} saved. The website will pick it up shortly.`);
      await reload();
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Could not save the page');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Legal pages"
        description="Privacy, terms, refund and delivery pages on the public website. Edit the wording here; it is stored in the database."
        actions={<><Button href={`${previewUrl}${current?.path || '/privacy'}`} target="_blank">Open on website</Button><BackToSettings /></>}
      />
      <ErrorNotice error={error} />
      {loading && !data ? <FormSkeleton sections={2} /> : (
        <>
          <Alert type="info" showIcon style={{ marginBottom: 20 }} message="Use Markdown: ## headings, - lists, **bold** and [links](/contact). Changes appear after the website’s short public cache expires (up to five minutes)." />
          <Panel title="Pages" description={current?.updatedAt ? `This page last saved ${timeAgo(current.updatedAt)}.` : 'These start with Kivonex defaults until you save your own wording.'}>
            <Tabs
              activeKey={current?.slug || 'privacy'}
              onChange={setSlug}
              items={pages.map((page) => ({ key: page.slug, label: page.title }))}
            />
            <Form form={form} layout="vertical" onFinish={save} requiredMark="optional">
              <Form.Item name="title" label="Page title" rules={[{ required: true, whitespace: true, max: 160, message: 'Enter the page title.' }]}>
                <Input placeholder="Privacy policy" />
              </Form.Item>
              <Form.Item name="body" label="Content" rules={[{ required: true, whitespace: true, message: 'Enter the page content.' }]}>
                <Input.TextArea rows={22} placeholder="Write the policy in Markdown" style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13, lineHeight: 1.55 }} />
              </Form.Item>
              <div className="form-actions">
                <Button type="primary" htmlType="submit" loading={saving}>Save {current?.title || 'page'}</Button>
                <Button href={`${previewUrl}${current?.path || '/privacy'}`} target="_blank">Preview</Button>
              </div>
            </Form>
          </Panel>
        </>
      )}
    </>
  );
}
